/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  FormatPaint as FormatPaintIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' }
];

const DEFAULT_BRANDING = {
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  accentColor: '#9c27b0',
  logoUrl: '',
  logoStoragePath: '',
  headerImageUrl: '',
  headerImageStoragePath: '',
  footerImageUrl: '',
  footerImageStoragePath: '',
  brandGuidelines: {
    fontFamily: 'Arial, sans-serif',
    headingFontFamily: 'Georgia, serif',
    fontSize: {
      h1: 32,
      h2: 28,
      h3: 24,
      h4: 20,
      h5: 18,
      h6: 16,
      body: 14,
      small: 12
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      bold: 700
    }
  },
  customCSS: ''
};

// eslint-disable-next-line max-lines-per-function
export default function EntityBrandingManager({ entityId, entityName, onSave }) {
  useAuth();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (entityId) {
      fetchBranding();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await api.getEntityBranding(entityId);
      if (response) {
        setBranding({ ...DEFAULT_BRANDING, ...response });
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
      showSnackbar('Failed to fetch entity branding', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateEntityBranding(entityId, branding);
      showSnackbar('Entity branding saved successfully', 'success');
      if (onSave) {
        onSave(branding);
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      showSnackbar('Failed to save entity branding', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (field, value) => {
    setBranding({ ...branding, [field]: value });
  };

  const handleFontChange = (field, value) => {
    setBranding({
      ...branding,
      brandGuidelines: {
        ...branding.brandGuidelines,
        [field]: value
      }
    });
  };

  const handleFontSizeChange = (heading, value) => {
    setBranding({
      ...branding,
      brandGuidelines: {
        ...branding.brandGuidelines,
        fontSize: {
          ...branding.brandGuidelines.fontSize,
          [heading]: value
        }
      }
    });
  };

  const handleImageUpload = async (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/svg+xml';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setSaving(true);
        const formData = new FormData();
        formData.append('image', file);
        formData.append('entityId', entityId);
        formData.append('imageType', field);

        const response = await api.uploadBrandImage(formData);

        // Update branding state with new image URL
        setBranding({
          ...branding,
          [`${field}Url`]: response.imageUrl,
          [`${field}StoragePath`]: response.storagePath
        });

        showSnackbar(`${field} image uploaded successfully`, 'success');
      } catch (error) {
        console.error('Image upload failed:', error);
        showSnackbar(`Failed to upload ${field} image`, 'error');
      } finally {
        setSaving(false);
      }
    };
    input.click();
  };


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const renderColorPicker = (label, field, icon) => {
    const validateHexColor = (value) => {
      return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
    };

    const handleHexInput = (value) => {
      // Add # if not present
      if (value && !value.startsWith('#')) {
        value = '#' + value;
      }

      // Update immediately for valid hex codes, or when empty
      if (!value || validateHexColor(value)) {
        handleColorChange(field, value);
      }
    };

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={branding[field]}
            onChange={(e) => handleHexInput(e.target.value)}
            placeholder="#1976d2"
            error={branding[field] && !validateHexColor(branding[field])}
            helperText={branding[field] && !validateHexColor(branding[field]) ? 'Invalid hex color' : 'Enter hex code (e.g. #1976d2)'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: validateHexColor(branding[field]) ? branding[field] : '#ccc',
                      border: '1px solid #ccc',
                      borderRadius: 1
                    }}
                  />
                </InputAdornment>
              )
            }}
          />
          <input
            type="color"
            value={validateHexColor(branding[field]) ? branding[field] : '#1976d2'}
            onChange={(e) => handleColorChange(field, e.target.value)}
            style={{
              width: 50,
              height: 36,
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderPreview = () => (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              color: branding.primaryColor,
              fontFamily: branding.brandGuidelines.headingFontFamily,
              fontSize: branding.brandGuidelines.fontSize.h1 + 'px',
              fontWeight: branding.brandGuidelines.fontWeight.bold
            }}
          >
            {entityName || 'Entity Name'}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: branding.secondaryColor,
              fontFamily: branding.brandGuidelines.headingFontFamily,
              fontSize: branding.brandGuidelines.fontSize.h2 + 'px',
              mt: 2
            }}
          >
            Sample Heading
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: branding.brandGuidelines.fontFamily,
            fontSize: branding.brandGuidelines.fontSize.body + 'px',
            mb: 2
          }}
        >
          This is sample body text showing how your content will appear with the current branding settings.
          The primary color is used for main headings and important elements.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="contained" sx={{ backgroundColor: branding.primaryColor }}>
            Primary Button
          </Button>
          <Button variant="contained" sx={{ backgroundColor: branding.secondaryColor }}>
            Secondary Button
          </Button>
          <Button variant="outlined" sx={{ color: branding.accentColor, borderColor: branding.accentColor }}>
            Accent Button
          </Button>
        </Box>

        <Alert severity="info" sx={{ '& .MuiAlert-icon': { color: branding.accentColor } }}>
          This is an informational message using your accent color.
        </Alert>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Loading entity branding...</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon />
          Entity Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize the visual appearance of templates and reports for {entityName || 'this entity'}.
        </Typography>

        <Grid container spacing={3}>
          {/* Colors Section */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormatPaintIcon />
              Brand Colors
            </Typography>
            <Stack spacing={2}>
              {renderColorPicker('Primary Color', 'primaryColor', <Box />)}
              {renderColorPicker('Secondary Color', 'secondaryColor', <Box />)}
              {renderColorPicker('Accent Color', 'accentColor', <Box />)}
            </Stack>
          </Grid>

          {/* Typography Section */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="h6" gutterBottom>
              Typography
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Body Font Family</InputLabel>
                <Select
                  value={branding.brandGuidelines.fontFamily}
                  onChange={(e) => handleFontChange('fontFamily', e.target.value)}
                  label="Body Font Family"
                >
                  {FONT_FAMILIES.map(font => (
                    <MenuItem key={font.value} value={font.value}>
                      <span style={{ fontFamily: font.value }}>{font.label}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Heading Font Family</InputLabel>
                <Select
                  value={branding.brandGuidelines.headingFontFamily}
                  onChange={(e) => handleFontChange('headingFontFamily', e.target.value)}
                  label="Heading Font Family"
                >
                  {FONT_FAMILIES.map(font => (
                    <MenuItem key={font.value} value={font.value}>
                      <span style={{ fontFamily: font.value }}>{font.label}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid>

          {/* Font Sizes Section */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Font Sizes
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(branding.brandGuidelines.fontSize).map(([key, value]) => (
                <Grid
                  key={key}
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 3
                  }}>
                  <Typography variant="caption" gutterBottom display="block">
                    {key.toUpperCase()} ({value}px)
                  </Typography>
                  <Slider
                    value={value}
                    onChange={(e, newValue) => handleFontSizeChange(key, newValue)}
                    min={10}
                    max={48}
                    step={1}
                    valueLabelDisplay="auto"
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Images Section */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon />
              Brand Images
            </Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Logo
                    </Typography>
                    {branding.logoUrl ? (
                      <Avatar
                        src={branding.logoUrl}
                        sx={{ width: 100, height: 100, mx: 'auto' }}
                        variant="rounded"
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          backgroundColor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          borderRadius: 1
                        }}
                      >
                        <ImageIcon color="disabled" />
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => handleImageUpload('logo')}
                    >
                      Upload
                    </Button>
                    {branding.logoUrl && (
                      <IconButton
                        size="small"
                        onClick={() => setBranding({ ...branding, logoUrl: '', logoStoragePath: '' })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </CardActions>
                </Card>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Header Image
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 60,
                        backgroundColor: branding.headerImageUrl ? 'transparent' : 'grey.200',
                        backgroundImage: branding.headerImageUrl ? `url(${branding.headerImageUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1
                      }}
                    >
                      {!branding.headerImageUrl && <ImageIcon color="disabled" />}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => handleImageUpload('headerImage')}
                    >
                      Upload
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Footer Image
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 60,
                        backgroundColor: branding.footerImageUrl ? 'transparent' : 'grey.200',
                        backgroundImage: branding.footerImageUrl ? `url(${branding.footerImageUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1
                      }}
                    >
                      {!branding.footerImageUrl && <ImageIcon color="disabled" />}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => handleImageUpload('footerImage')}
                    >
                      Upload
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Custom CSS Section */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Custom CSS
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={branding.customCSS}
              onChange={(e) => setBranding({ ...branding, customCSS: e.target.value })}
              placeholder="/* Add custom CSS rules here */"
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setBranding(DEFAULT_BRANDING)}
          >
            Reset to Defaults
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewOpen(!previewOpen)}
            >
              {previewOpen ? 'Hide' : 'Show'} Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save Branding
            </Button>
          </Box>
        </Box>
      </Paper>
      {previewOpen && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Branding Preview
          </Typography>
          {renderPreview()}
        </Paper>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}